import { useCallback, useMemo } from 'react';
import { pick, uniqBy } from 'lodash-es';
import { useRouteResources } from '@lp/features/RoutePlanner/hooks/useRouteResources';
import { useLogisticsData } from '@lp/features/RoutePlanner/hooks/useLogisticsProjection';
import { useRouteMainLocations } from '@lp/features/RoutePlanner/hooks/useRouteMainLocations';
import { CustomLocationType, LegLocationTypeEnum, RouteLocationType } from '@/api/apis/route';
import { DataCacheType } from '@/queries/consts';

export const useRouteLocations = (type: DataCacheType = DataCacheType.Live) => {
  const mainLocations = useRouteMainLocations();
  const logistics = useLogisticsData();
  const { ClinicalSpecialist: csList, Surgeon: sList, OCSConsole: deviceList } = useRouteResources(type);

  const locations = useMemo<RouteLocationType[]>(() => {
    const csLocations = csList.flatMap<RouteLocationType>((item) => {
      const itemLocations: RouteLocationType[] = [];

      if (item.data?.homeLocation) {
        itemLocations.push({
          id: item.data.homeLocation.id,
          locationType: LegLocationTypeEnum.homeResidence,
          location: pick(item.data.homeLocation, [
            'country',
            'state',
            'city',
            'postalCode',
            'addressLine',
            'latitude',
            'longitude',
            'timezone',
          ]) as CustomLocationType,
        } as RouteLocationType);
      }

      if (item.data?.baseLocation) {
        itemLocations.push({
          id: item.data.baseLocation.id,
          locationType: LegLocationTypeEnum.hub,
          name: item.data.baseLocation.name,
          code: item.data.baseLocation.code,
          location: item.data.baseLocation.location,
        } as RouteLocationType);
      }

      return itemLocations;
    });

    const sLocations = sList.flatMap<RouteLocationType>((item) => {
      if (!item.data?.homeLocation) return [];

      return [
        {
          id: item.data.homeLocation.id,
          locationType: LegLocationTypeEnum.homeResidence,
          location: pick(item.data.homeLocation, [
            'country',
            'state',
            'city',
            'postalCode',
            'addressLine',
            'latitude',
            'longitude',
            'timezone',
          ]) as CustomLocationType,
        } as RouteLocationType,
      ];
    });

    const deviceLocations = deviceList.flatMap<RouteLocationType>((item) => {
      if (!item.data?.hub) return [];

      return [
        {
          id: item.data.hub.id,
          locationType: LegLocationTypeEnum.hub,
          name: item.data.hub.name,
          code: item.data.hub.code,
          location: item.data.hub.location,
        } as RouteLocationType,
      ];
    });

    const list = [
      ...mainLocations,
      ...logistics.locations,
      ...csLocations,
      ...sLocations,
      ...deviceLocations,
    ] as RouteLocationType[];

    return uniqBy<RouteLocationType>(list, 'id');
  }, [mainLocations, logistics.locations, csList, sList, deviceList]);

  const getLocationById = useCallback(
    (locationId?: string) => {
      return locations.find((location) => location.id === locationId);
    },
    [locations],
  );

  return useMemo(
    () => ({
      caseLocations: mainLocations,
      list: locations,
      getLocationById,
    }),
    [mainLocations, locations, getLocationById],
  );
};
